import { getProductsWithCouriers } from '@/API/repositories/lms';
import ActionButton from '@/common/components/buttons/ActionButton';
import SelectInput from '@/common/components/SelectInput';
import SupportMail from '@/common/components/supportMail/SupportMail';
import { useMessageQueueContext } from '@/common/hooks/useMessageQueue';
import { useTranslationContext } from '@/common/hooks/useTranslationContext';
import { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
	addProductToOrder,
	markOrdertAsConsultantDecision,
	sendOnlinePaymentLink,
	updateOrderByConsultant,
} from '../../../../../API/repositories/order';
import { Colors } from '../../../../../common/colors/colors';
import Input from '../../../../../common/components/Input';
import Loading from '../../../../../common/components/Loading';
import PopUp from '../../../../../common/components/PopUp';
import { formatToDateTamplate } from '../../../../../common/functions/dateFormater';
import { useRequestsContext } from '../../../../../common/hooks/requestHook';
import MoreInfo from '../../moreInfo/MoreInfo';
import AddVariant from './AddVariant';
import ItemsPicker from './ItemsPicker';
import { getPostsMap } from '@/common/functions/getPostsMap';
import moment from 'moment';
import { useCheckShippingLimit } from '@/common/hooks/useCheckShippingLimit';
import { DEFAULT_DATE_FORMAT } from '@/common/constants/dateFormat';
import { AuthContext } from '@/common/contexts/authContext';


const Header = styled.div`
	display: flex;
	max-width: 550px;
	gap: 1rem;

	h2 {
		color: ${Colors.darkBlue};
	}
`;

const ActionButtons = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 10px;
`;

const DisplayFlex = styled.div`
	margin-top: -20px;
	display: flex;
	justify-content: space-between;
	align-items: center;

	button {
		margin: 0 30px;
	}
`;

const OrderShow = ({ order, setOrder, contact, handleLoadContact }) => {
	const { authUser } = useContext(AuthContext);

	const [addVariant, setAddVariant] = useState();
	const [selectedCourier, setSelectedCourier] = useState();
	const [courierOptions, setCourierOptions] = useState();
	const [productsWithCouriers, setProductsWithCouriers] = useState();

	const suggestedDeliveryDateRef = useRef();
	const { hasUnfilledRequest, makeRequest } = useRequestsContext();
	const { addMessage } = useMessageQueueContext();
	const { translation } = useTranslationContext();

	const { checkShippingLimit, minLimit, threeWeeksFromNowShippingLimit } =
		useCheckShippingLimit();

	const hadnleDelteProductItem = async (e, number) => {
		const valdated = order.order_items
			.filter((e, i) => i !== number)
			.map((e, i) => ({
				product: e.product,
				value: e.product_variant,
				price: e.price,
			}));

		const response = await makeRequest(
			addProductToOrder.bind(null, order._id, valdated),
		);

		if (!response?.data) {
			return addMessage(
				'You are not allowed to update this order, please, quickly contact CC Manager',
				'error',
			);
		}

		await handleLoadContact();
	};

	const loadCouriers = async () => {
		setSelectedCourier(() => null);
		setCourierOptions(() => []);

		const response = await makeRequest(getProductsWithCouriers);

		if (response?.data) {
			setProductsWithCouriers(response.data);
		}
	};

	const handleSetCouriersData = () => {
		if (!productsWithCouriers) {
			return;
		}

		const currentProductWithCourier = productsWithCouriers.find(
			(productWithCourier) => order._product.short === productWithCourier.short,
		);

		if (!currentProductWithCourier) {
			return;
		}

		const postsMap = getPostsMap(currentProductWithCourier);

		const options = Array.from(postsMap).map(([label, value]) => ({
			label,
			value,
		}));

		setCourierOptions(options);

		setSelectedCourier(
			options.find(
				(option) => option.value === order?.shipping?.curier_lms_id,
			) || options[0],
		);
	};

	const handleChangeShippingDate = async (e) => {
		e.preventDefault();

		const deliveryDate = suggestedDeliveryDateRef.current?.value
			? moment(suggestedDeliveryDateRef.current.value)
			: null;

		const isShippingDateValid = checkShippingLimit(deliveryDate);

		if (!isShippingDateValid) {
			return;
		}

		order.shipping.suggested_delivery_date =
			suggestedDeliveryDateRef.current.value;
		order.items = order.order_items?.map((item) => ({
			product: item.product,
			price: item.price,
			value: item.product_variant,
		}));
		order.shipping.full_name = contact.full_name;
		order.shipping.email = contact.email;
		order.shipping.phone_number = contact.phone_number;
		order.shipping.street = contact.street;
		order.shipping.postal_code = contact.postal_code;
		order.shipping.city = contact.city;
		order.shipping.country = contact.country;
		order.shipping.curier_lms_id = selectedCourier?.value;

		const response = await makeRequest(
			updateOrderByConsultant.bind(null, order._id, order),
		);

		if (!response.data) {
			return addMessage(
				'You are not allowed to update this order, please, quickly contact CC Manager',
				'error',
			);
		}

		await handleLoadContact();
	};

	const handleOnlinePayment = async () => {
		const response = await makeRequest(
			sendOnlinePaymentLink.bind(null, order._id),
		);

		if (!response.data) {
			return addMessage('An unexpected error occurred', 'error');
		}

		await makeRequest(
			markOrdertAsConsultantDecision.bind(
				this,
				authUser.id,
				order._id,
				{ type: 'CONFIRM', additionalInformation: {} },
			),
		);

		addMessage('The payment link has been sent.', 'success');
	};

	const handleChangeCourier = async (e) => {
		e.preventDefault();

		if (!order.shipping) {
			return;
		}

		order.shipping.full_name = contact.full_name;
		order.shipping.email = contact.email;
		order.shipping.phone_number = contact.phone_number;
		order.shipping.street = contact.street;
		order.shipping.postal_code = contact.postal_code;
		order.shipping.city = contact.city;
		order.shipping.country = contact.country;
		order.items = order.order_items?.map((item) => ({
			product: item.product,
			price: item.price,
			value: item.product_variant,
		}));
		order.shipping.curier_lms_id = selectedCourier?.value;

		const response = await makeRequest(
			updateOrderByConsultant.bind(null, order._id, order),
		);

		if (!response.data) {
			return addMessage(
				'You are not allowed to update this order, please, quickly contact CC Manager',
				'error',
			);
		}

		await handleLoadContact();
	};

	useEffect(() => {
		loadCouriers();
	}, []);

	useEffect(() => {
		handleSetCouriersData();
	}, [productsWithCouriers]);

	return (
		<>
			{hasUnfilledRequest(
				addProductToOrder,
				getProductsWithCouriers,
				updateOrderByConsultant,
			) && <Loading />}
			<PopUp setShow={setOrder}>
				<Header>
					<h2>{translation.order}:</h2>
					<ActionButtons>
						<MoreInfo
							product={order?._product?.short}
							email={contact.email}
							sendMail={translation.sendMail}
							order={order}
							mailError={translation.mailError}
							mailSuccess={translation.mailSuccess}
						/>
						<SupportMail order={order} />

						<ActionButton
							color={Colors.blue}
							onClick={handleOnlinePayment}
							defaultText={translation.sendPaymentMail}
						/>
					</ActionButtons>
				</Header>

				<DisplayFlex style={{ marginTop: '20px' }}>
					<div>
						<ItemsPicker
							market={order._market}
							product={order._product}
							chosenVariants={order.order_items}
							setAddVariant={setAddVariant}
							hadnleDelteProductItem={hadnleDelteProductItem}
						/>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								margin: '20px 0 0',
							}}
						>
							<SelectInput
								width={80}
								selectWidth={230}
								name={translation.curier}
								color={Colors.darkBlue}
								options={courierOptions}
								setSelected={setSelectedCourier}
								selected={selectedCourier}
							/>
							<ActionButton
								onClick={(e) => handleChangeCourier(e)}
								disabled={
									order.shipping?.waybill_number || order.status !== 'confirmed'
								}
								defaultText={translation.changeCurier}
							/>
						</div>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								margin: '20px 0 0',
							}}
						>
							<Input
								name={translation.suggested_delivery}
								color={Colors.darkBlue}
								inputRef={suggestedDeliveryDateRef}
								value={
									order.shipping?.suggested_delivery_date &&
									formatToDateTamplate(order.shipping.suggested_delivery_date)
								}
								width={180}
								type='date'
								inputWidth={130}
								max={threeWeeksFromNowShippingLimit.format(DEFAULT_DATE_FORMAT)}
								min={minLimit.format(DEFAULT_DATE_FORMAT)}
							/>
							<ActionButton
								onClick={(e) => handleChangeShippingDate(e)}
								disabled={order.status !== 'confirmed'}
								defaultText={translation.changeDate}
							/>
						</div>
					</div>
				</DisplayFlex>
			</PopUp>
			{addVariant && (
				<AddVariant
					product={order._product}
					setAddVariant={setAddVariant}
					order={order}
					handleLoadContact={handleLoadContact}
				/>
			)}
		</>
	);
};

export default OrderShow;
